/**
 * The floating "Back to Top" button. It appears once the page has scrolled more than one window
 * height and smooth-scrolls to the top when clicked. It sits bottom right, and bottom left below
 * the theme's `lg` breakpoint.
 *
 * Rendered by `components/NextLayouts/HeaderLayout.tsx`, so on every page except the three
 * redirect-only pages that don't render `HeaderLayout` (listed in its header). The label comes from
 * `BackToTopContent.tsx` in the visitor's language.
 */
import React from "react";
import styled from "@emotion/styled";
import { Typography } from "@ryerson/frontend.typography";
import { Icon } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { BackToTopContent } from "./BackToTopContent";
import { useApplication } from "@ryerson/frontend.application";

const ButtonContainer = styled.div`
	position: fixed;
	bottom: 20px;
	right: 20px;
	border-radius: 20px;
	width: auto;
	height: 42px;
	cursor: pointer;
`;

/**
 * The shape of each language's entry in `BackToTopContent.tsx`. As a prop, `buttonLabel` is
 * ignored: `HeaderLayout.tsx` passes `""`, and the component reads the label from
 * `BackToTopContent`.
 */
export interface BackToTopProps {
	buttonLabel: string;
}

/** The floating "Back to Top" button; see the file header. */
const BackToTop: React.FC<BackToTopProps> = () => {
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();
	const [isVisible, setIsVisible] = React.useState(false);

	const toggleVisibility = () => {
		if (window.scrollY > window.innerHeight) {
			setIsVisible(true);
		} else {
			setIsVisible(false);
		}
	};

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	React.useEffect(() => {
		window.addEventListener("scroll", toggleVisibility);
		return () => {
			window.removeEventListener("scroll", toggleVisibility);
		};
	}, []);

	return (
		<>
			{isVisible && (
				<ButtonContainer
					onClick={scrollToTop}
					css={css`
						background-color: ${theme.colors.primary.lightGray};
						z-index: 1000;
						@media (max-width: ${theme.breakpoints.lg}) {
							right: unset;
							left: 20px;
						}
					`}
				>
					<Typography
						variant="div"
						size="sm"
						weight="bold"
						type="secondary"
						color={theme.colors.secondary.header}
						css={css`
							white-space: nowrap;
							height: 100%;
							padding: 0px 14px;
							line-height: 42px;
							@media (max-width: ${theme.breakpoints.lg}) {
								right: unset;
								line-height: 42px;
							}
						`}
					>
						<Icon
							css={css`
								margin-right: 10px;
							`}
							icon="chevron-up"
							color={theme.colors.secondary.header}
							size="xs"
						/>
						{BackToTopContent[language].buttonLabel}
					</Typography>
				</ButtonContainer>
			)}
		</>
	);
};

export default BackToTop;
